import { checkedRpcResponse } from './rpc-http-response.js';
import type { AppRouter } from '@keji/trpc';
import {
  PERSISTENT_OPERATION_PROCEDURES,
  createPersistentOperationRegistry,
} from '@keji/api-client';
import { QueryClient } from '@tanstack/react-query';
import { TRPCClientError, createTRPCClient, httpLink } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';

import { clearSession, readSession, sessionActivity } from './auth.js';
import { safeUserMessage } from './error-message.js';

/**
 * 与后端的唯一通道。
 *   · 每个写请求带一把新的幂等键(铁律 5);重试由 TanStack Query 的 retry 承担,键在同一次 mutate 内不变
 *   · 401 一律清登录态回登录页 —— **在传输层做**,见 onUnauthenticated
 */

/**
 * 会话失效的唯一处置点。
 *
 * ⚠ 以前这件事挂在 `describeError` 的副作用里 —— 只有页面**选择渲染错误**时才会执行。
 *   而页面把 401 吞掉当空数据渲染时(工作台就是这样),什么也不会发生:
 *   业务员看到的不是「请重新登录」,而是一个所有数字都是 0 的正常看板,
 *   他会以为今天没事干。这类缺陷不报错、不告警。
 *
 * ⚠ 只在**本来就有登录态**时才跳:登录页输错口令同样是 401,
 *   那不是会话失效,不该把人从登录页再踢一次。
 */
let redirecting = false;
function onUnauthenticated(): void {
  if (redirecting) return;
  if (window.location.pathname === '/login') return;
  redirecting = true;
  clearSession();
  window.location.assign('/login');
}
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 10_000 },
    mutations: { retry: 0 },
  },
});

const mutationKeys = new WeakMap<object, string>();
const persistentOperations = createPersistentOperationRegistry(localStorage);

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpLink({
      url: '/trpc',
      // ⚠ 会话失效在这里统一处置:任何一个过程调用拿到 401 都算数,
      //   不依赖调用方是否记得处理错误
      fetch: async (input, init) => {
        const res = await fetch(input, init);
        persistentOperations.settleFromResponse(init?.headers, res.status);
        if (res.status === 401) onUnauthenticated();
        return checkedRpcResponse(res);
      },
      headers: ({ op }) => {
        const session = readSession();
        const headers: Record<string, string> = {};
        if (session) {
          headers.authorization = `Bearer ${session.token}`;
          if (sessionActivity.recent()) headers['x-session-activity'] = String(sessionActivity.age());
        }
        if (op.type === 'mutation') {
          if (PERSISTENT_OPERATION_PROCEDURES.has(op.path)) {
            const namespace = session
              ? `${session.tenant.id}:${session.user.id}:${session.user.role}`
              : 'anonymous';
            headers['idempotency-key'] = persistentOperations.pin(namespace, op.path, op.input);
            return headers;
          }
          // 同一次调用对象复用同一把键;库内重试时不会换键
          let key = mutationKeys.get(op);
          if (!key) {
            key = `web-${crypto.randomUUID()}`;
            mutationKeys.set(op, key);
          }
          headers['idempotency-key'] = key;
        }
        return headers;
      },
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

/**
 * 把 tRPC 错误翻译成可读文案。
 * ⚠ **只负责文案**。登出与跳转由 onUnauthenticated 在传输层负责 ——
 *   把安全动作挂在格式化函数的副作用上,页面不调它就等于没有
 */
/** 服务端给的领域错误码(CONFLICT / CAPTCHA_REQUIRED / …);不是 tRPC 错误时为 null */
export function domainCodeOf(error: unknown): string | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data = error.data as { domainCode?: string } | undefined;
  return data?.domainCode ?? null;
}

export function describeError(error: unknown): string {
  if (error instanceof TRPCClientError) {
    const data = error.data as { domainCode?: string; hint?: string | null } | undefined;
    const message = safeUserMessage(error.message);
    return data?.hint ? `${message}（${data.hint}）` : message;
  }
  return safeUserMessage(error instanceof Error ? error.message : String(error));
}
